import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { Visit } from '../types';

const CustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { shop } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [stats, setStats] = useState({
    totalVisits: 0,
    totalSpent: 0,
    averageSpent: 0,
    lastVisit: null as Date | null,
  });
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (!shop || !customerId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 顧客情報を取得（usersコレクションから）
        const userDoc = await getDoc(doc(db, 'users', customerId));
        if (!userDoc.exists()) {
          setError('顧客情報が見つかりません');
          return;
        }

        const userData = {
          id: userDoc.id,
          ...userDoc.data(),
          createdAt: userDoc.data().createdAt?.toDate(),
          updatedAt: userDoc.data().updatedAt?.toDate(),
        };
        setCustomer(userData);

        // 来店履歴を取得
        const visitsQuery = query(
          collection(db, 'visits'),
          where('shopId', '==', shop.id),
          where('userId', '==', customerId),
          orderBy('visitDate', 'desc'),
          limit(50)
        );
        const visitsSnapshot = await getDocs(visitsQuery);
        const visitsData = visitsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          visitDate: doc.data().visitDate?.toDate(),
          firstOrderAt: doc.data().firstOrderAt?.toDate(),
          lastOrderAt: doc.data().lastOrderAt?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as Visit[];
        setVisits(visitsData);

        // 統計情報を計算
        const totalVisits = visitsData.length;
        const totalSpent = visitsData.reduce((sum, visit) => sum + (visit.totalAmount || 0), 0);
        const averageSpent = totalVisits > 0 ? totalSpent / totalVisits : 0;
        const lastVisit = visitsData.length > 0 ? visitsData[0].visitDate : null;

        setStats({
          totalVisits,
          totalSpent,
          averageSpent,
          lastVisit,
        });

        // 店舗メモを取得
        setNotes((userData as any).shopNotes?.[shop.id] || '');
      } catch (err: any) {
        console.error('Error fetching customer data:', err);
        setError(err.message || 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shop, customerId]);

  const handleSaveNotes = async () => {
    if (!customer || !shop) return;

    try {
      setSavingNotes(true);
      const userRef = doc(db, 'users', customer.id);
      await updateDoc(userRef, {
        [`shopNotes.${shop.id}`]: notes,
        updatedAt: Timestamp.now(),
      });
      alert('メモを保存しました');
    } catch (err: any) {
      console.error('Error saving notes:', err);
      alert('メモの保存に失敗しました');
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || '顧客情報が見つかりません'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mt: 2 }}>
          顧客一覧に戻る
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mb: 2 }}>
        顧客一覧に戻る
      </Button>

      {/* 顧客基本情報 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={customer.photoUrl}
            alt={customer.displayName}
            sx={{ width: 80, height: 80, mr: 3 }}
          >
            {customer.displayName?.charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom>
              {customer.profile?.fullName || customer.displayName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {customer.authType === 'line' && (
                <Chip label="LINE認証" color="success" size="small" />
              )}
              {customer.authType === 'sms' && (
                <Chip label="SMS認証" color="primary" size="small" />
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {(customer.profile?.phone || customer.phoneNumber) && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography>{customer.profile?.phone || customer.phoneNumber}</Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography>
                登録日: {customer.createdAt?.toLocaleDateString('ja-JP')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 統計情報 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                総来店回数
              </Typography>
              <Typography variant="h4">{stats.totalVisits}回</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                総利用金額
              </Typography>
              <Typography variant="h4">¥{stats.totalSpent.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                平均利用金額
              </Typography>
              <Typography variant="h4">¥{Math.round(stats.averageSpent).toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                最終来店日
              </Typography>
              <Typography variant="h6">
                {stats.lastVisit ? stats.lastVisit.toLocaleDateString('ja-JP') : 'なし'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 店舗メモ */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          店舗メモ
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          この顧客に関するメモを記録できます（顧客には表示されません）
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="例: アレルギー情報、好みの席、その他特記事項"
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleSaveNotes}
          disabled={savingNotes}
        >
          {savingNotes ? '保存中...' : 'メモを保存'}
        </Button>
      </Paper>

      {/* 来店履歴 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          来店履歴
        </Typography>
        {visits.length === 0 ? (
          <Alert severity="info">来店履歴がありません</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>来店日</TableCell>
                  <TableCell>種別</TableCell>
                  <TableCell>詳細</TableCell>
                  <TableCell align="right">金額</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visits.map((visit) => (
                  <TableRow key={visit.id}>
                    <TableCell>
                      {visit.visitDate.toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                      })}
                    </TableCell>
                    <TableCell>
                      {visit.visitType === 'reservation' ? (
                        <Chip label="予約" color="primary" size="small" />
                      ) : (
                        <Chip label="モバイルオーダー" color="secondary" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {visit.visitType === 'reservation' ? (
                        <>
                          {visit.menuNames && visit.menuNames.length > 0 && (
                            <Typography variant="body2">
                              {visit.menuNames.join(', ')}
                            </Typography>
                          )}
                          {visit.staffName && (
                            <Typography variant="caption" color="text.secondary">
                              担当: {visit.staffName}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          {visit.tableNumber && (
                            <Typography variant="body2">
                              テーブル: {visit.tableNumber}
                            </Typography>
                          )}
                          {visit.orderIds && (
                            <Typography variant="caption" color="text.secondary">
                              注文数: {visit.orderIds.length}件
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {visit.totalAmount ? `¥${visit.totalAmount.toLocaleString()}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default CustomerDetail;
